import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import type { FlagsStateContainer } from '@confluence/flags';

import { BulkSetRoleAssignmentToSpacesMutation } from './graphql/BulkSetRoleAssignmentToSpacesMutation.graphql';
import type {
	BulkSetRoleAssignmentToSpaces_bulkSetRoleAssignmentToSpaces as BulkSetRoleAssignmentToSpacesResponse,
	BulkSetRoleAssignmentToSpacesVariables,
} from './graphql/__types__/BulkSetRoleAssignmentToSpacesMutation';
import { BulkRemoveRoleAssignmentFromSpacesMutation } from './graphql/BulkRemoveRoleAssignmentFromSpacesMutation.graphql';
import type {
	BulkRemoveRoleAssignmentFromSpaces_bulkRemoveRoleAssignmentFromSpaces as BulkRemoveRoleAssignmentFromSpacesResponse,
	BulkRemoveRoleAssignmentFromSpacesVariables,
} from './graphql/__types__/BulkRemoveRoleAssignmentFromSpacesMutation';
import { useBulkRoleFlags } from './useBulkRoleFlags';

export const useBulkRoleSetAssignment = ({
	closeDialog,
	flags,
}: {
	closeDialog: () => void;
	flags: FlagsStateContainer;
}) => {
	const { showErrorFlag, showProcessingFlag } = useBulkRoleFlags({ flags });

	const onMutationErrored = useCallback(() => {
		closeDialog();
		showErrorFlag();
	}, [closeDialog, showErrorFlag]);

	const onAsyncMutationSubmitted = useCallback(
		(data) => {
			closeDialog();
			/**
			 * The backend is counting the mutation as a success for some exceptions
			 * but returning an error in the mutation response.  We need to check for
			 * these here.
			 */
			if (
				data?.bulkSetRoleAssignmentToSpaces?.errors?.length > 0 ||
				data?.bulkRemoveRoleAssignmentFromSpaces?.errors?.length > 0
			) {
				onMutationErrored();
			} else {
				if (
					data.bulkSetRoleAssignmentToSpaces?.taskId ||
					data.bulkRemoveRoleAssignmentFromSpaces?.taskId
				) {
					// show the progress flag
					void showProcessingFlag();
				}
			}
		},
		[closeDialog, showProcessingFlag, onMutationErrored],
	);

	const [bulkSetRoleAssignmentToSpaces, { data: bulkAssignmentData }] = useMutation<
		BulkSetRoleAssignmentToSpacesResponse,
		BulkSetRoleAssignmentToSpacesVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
		BulkSetRoleAssignmentToSpacesMutation,
		{
			onCompleted: onAsyncMutationSubmitted,
			onError: () => {
				closeDialog();
				onMutationErrored();
			},
		},
	);

	const [bulkRemoveRoleAssignment, { data: bulkRemoveAssignmentData }] = useMutation<
		BulkRemoveRoleAssignmentFromSpacesResponse,
		BulkRemoveRoleAssignmentFromSpacesVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations
		BulkRemoveRoleAssignmentFromSpacesMutation,
		{
			onCompleted: onAsyncMutationSubmitted,
			onError: () => {
				closeDialog();
				onMutationErrored();
			},
		},
	);

	return {
		bulkSetRoleAssignmentToSpaces,
		bulkRemoveRoleAssignment,
		bulkAssignmentData,
		bulkRemoveAssignmentData,
	};
};
